/**
 * @file 読んだ時間 (api/readingSessions.ts) を期間で読む hook。
 *
 * @what: まなぶ人と期間 [from, to] が決まったら1回読み、日付の昇順の配列を返す。読み込み中と失敗は
 *   区別して返す (学んだ日の暦は読み終えるまで出さない)。
 * @failure: 取得失敗は `error` (呼び手は暦を出さず1行で知らせる)。まなぶ人が未定・enabled でないときは `idle`。
 */

import { useEffect, useState } from "react";
import { fetchReadingSessions, type StudyReadingSession } from "../api/readingSessions";

export type ReadingSessionsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; sessions: StudyReadingSession[] }
  | { status: "error" };

export function useReadingSessions(
  familyId: string,
  personId: string | null,
  from: string,
  to: string,
  enabled = true,
): ReadingSessionsState {
  const [state, setState] = useState<ReadingSessionsState>({ status: "idle" });

  useEffect(() => {
    if (!enabled || !personId) {
      setState({ status: "idle" });
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchReadingSessions(familyId, personId, from, to, controller.signal)
      .then((sessions) => {
        if (!controller.signal.aborted) setState({ status: "ready", sessions });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load reading sessions", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, personId, from, to, enabled]);

  return state;
}
